import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import {
  ClockIcon,
  UserIcon,
  ExclamationCircleIcon,
  PauseIcon,
  LightBulbIcon,
  CheckCircleIcon,
  StopIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { stripHtml } from '../utils/sanitize';

interface Activity {
  id: string;
  activityType: string;
  previousState?: string;
  newState?: string;
  metadata?: any;
  performedAt: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ActivitiesTabProps {
  caseId: string;
}

const STATE_LABELS = {
  new: 'New',
  open: 'Open',
  in_progress: 'In Progress',
  awaiting_info: 'Awaiting Info',
  on_hold: 'On Hold',
  solution_proposed: 'Solution Proposed',
  closed: 'Closed',
  cancelled: 'Cancelled'
};

const STATE_COLORS = {
  new: 'bg-gray-500',
  open: 'bg-blue-500',
  in_progress: 'bg-yellow-500',
  awaiting_info: 'bg-purple-500',
  on_hold: 'bg-orange-500',
  solution_proposed: 'bg-indigo-500',
  closed: 'bg-green-500',
  cancelled: 'bg-red-500'
};

const STATE_ICONS = {
  new: UserIcon,
  open: UserIcon,
  in_progress: PlayIcon,
  awaiting_info: ExclamationCircleIcon,
  on_hold: PauseIcon,
  solution_proposed: LightBulbIcon,
  closed: CheckCircleIcon,
  cancelled: StopIcon
};

const ActivitiesTab: React.FC<ActivitiesTabProps> = ({ caseId }) => {
  const { data: activities, isLoading } = useQuery<Activity[]>({
    queryKey: ['case', caseId, 'activities'],
    queryFn: async () => {
      const response = await api.get(`/cases/${caseId}/activities`);
      return response.data;
    }
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex space-x-3">
              <div className="h-10 w-10 bg-gray-300 rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                <div className="h-3 bg-gray-300 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const formatStateChange = (activity: Activity) => {
    if (activity.activityType === 'state_change' && activity.previousState && activity.newState) {
      return `Changed state from ${STATE_LABELS[activity.previousState as keyof typeof STATE_LABELS] || activity.previousState} to ${STATE_LABELS[activity.newState as keyof typeof STATE_LABELS] || activity.newState}`;
    }
    return 'State change';
  };

  const renderActivityMetadata = (metadata: any) => {
    if (!metadata || typeof metadata !== 'object') return null;

    const details = [];
    
    if (metadata.infoRequired) {
      details.push(
        <div key="infoRequired" className="mt-2 p-3 bg-yellow-50 rounded border-l-4 border-yellow-200">
          <p className="text-sm font-medium text-yellow-800">Info Required:</p>
          <p className="text-sm text-yellow-700">{metadata.infoRequired}</p>
        </div>
      );
    }

    if (metadata.contactEmails && metadata.contactEmails.length > 0) {
      details.push(
        <div key="contactEmails" className="mt-2 p-3 bg-blue-50 rounded border-l-4 border-blue-200">
          <p className="text-sm font-medium text-blue-800">Contact Emails:</p>
          <p className="text-sm text-blue-700">{metadata.contactEmails.join(', ')}</p>
        </div>
      );
    }

    if (metadata.onHoldReason) {
      details.push(
        <div key="onHoldReason" className="mt-2 p-3 bg-orange-50 rounded border-l-4 border-orange-200">
          <p className="text-sm font-medium text-orange-800">On-Hold Reason:</p>
          <p className="text-sm text-orange-700">{stripHtml(metadata.onHoldReason)}</p>
        </div>
      );
    }

    if (metadata.solution) {
      details.push(
        <div key="solution" className="mt-2 p-3 bg-purple-50 rounded border-l-4 border-purple-200">
          <p className="text-sm font-medium text-purple-800">Proposed Solution:</p>
          <p className="text-sm text-purple-700">{stripHtml(metadata.solution)}</p>
        </div>
      );
    }

    if (metadata.resolutionCode) {
      details.push(
        <div key="resolution" className="mt-2 p-3 bg-green-50 rounded border-l-4 border-green-200">
          <p className="text-sm font-medium text-green-800">Resolution Details:</p>
          <p className="text-sm text-green-700 mb-2">
            <span className="font-medium">Code:</span> {metadata.resolutionCode.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
          </p>
          {metadata.cause && (
            <p className="text-sm text-green-700 mb-2">
              <span className="font-medium">Cause:</span> {stripHtml(metadata.cause)}
            </p>
          )}
          {metadata.resolutionNotes && (
            <p className="text-sm text-green-700">
              <span className="font-medium">Notes:</span> {stripHtml(metadata.resolutionNotes)}
            </p>
          )}
        </div>
      );
    }

    if (metadata.cancellationReason) {
      details.push(
        <div key="cancellation" className="mt-2 p-3 bg-red-50 rounded border-l-4 border-red-200">
          <p className="text-sm font-medium text-red-800">Cancellation Reason:</p>
          <p className="text-sm text-red-700">{stripHtml(metadata.cancellationReason)}</p>
        </div>
      );
    }

    if (metadata.communicateToCustomer) {
      details.push(
        <div key="communicate" className="mt-2 p-2 bg-indigo-50 rounded border-l-4 border-indigo-200">
          <p className="text-sm text-indigo-800">✓ Communicate to Customer/Contact Person</p>
        </div>
      );
    }

    return details.length > 0 ? <div className="space-y-1">{details}</div> : null;
  };

  if (!activities || activities.length === 0) {
    return (
      <div className="text-center py-12">
        <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No activities yet</h3>
        <p className="mt-1 text-sm text-gray-500">
          State changes and other activities will appear here as the case progresses.
        </p>
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {activities.map((activity, activityIdx) => {
          const StateIcon = STATE_ICONS[activity.newState as keyof typeof STATE_ICONS] || UserIcon;
          const stateColor = STATE_COLORS[activity.newState as keyof typeof STATE_COLORS] || 'bg-gray-500';
          const isLastItem = activityIdx === activities.length - 1;
          
          return (
            <li key={activity.id}>
              <div className="relative pb-8">
                {!isLastItem && (
                  <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                )}
                <div className="relative flex items-start space-x-3">
                  <div className="relative">
                    <span className={`h-10 w-10 rounded-full ${stateColor} flex items-center justify-center ring-8 ring-white`}>
                      <StateIcon className="h-5 w-5 text-white" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div>
                      <div className="text-sm">
                        <span className="font-medium text-gray-900">
                          {activity.User.firstName} {activity.User.lastName}
                        </span>{' '}
                        <span className="text-gray-600">
                          {formatStateChange(activity)}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-gray-500">
                        {new Date(activity.performedAt).toLocaleString()}
                      </p>
                    </div>
                    {renderActivityMetadata(activity.metadata)}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ActivitiesTab;